/* **Buddy's mark: the MoveBuddy mark cut from a matte pearl that turns.**
 *
 * The sibling of `brandMark.module.css`, and built the same way: the brand asset
 * (`public/brand/mark.png`) as a MASK, never a drawing of it. Canon: `docs/design/ai-buddy-2026-09-30/`,
 * boards N and O ("Znak Buddy'ego"). Four layers, all cut by the same mask:
 *
 *   · ghost      the mark's own silhouette in the ink at a tenth, so its shape reads while the pearl
 *                is at its darkest angle
 *   · ink        the pearl, a conic fill that turns: 9 s at rest, 1.8 s while Buddy works
 *   · arrival    the same turn in ember, shown once when an answer arrives and faded back to pearl
 *   · highlight  a fixed spot of light, so the turning fill reads as a surface rather than a spinner
 *
 * ── The turn is the canvas's own: an animated ANGLE inside the gradient ─────────────────────
 * Board N animates a registered `--ang` in `conic-gradient(from var(--ang) at 50% 45%, a, b, c, b,
 * a)`, and this does the same, one to one (owner, 2 October 2026). An earlier build rotated a
 * square of a static cone behind the mask instead; on the mark's thin strokes that read as almost
 * still, and a transform under a mask is not repainted by every engine. The angle is registered
 * HERE (`--pearl-ang`, non-inheriting, so each mark animates its own), and the cone is written here
 * from colour-stop tokens: a gradient kept whole in a token would resolve the angle where the token
 * is declared, on the theme class, once for every mark.
 *
 * ── Reduced motion ───────────────────────────────────────────────────────────────────────────
 * `tokens/base.css` collapses every loop to one instant iteration, so each animation below is
 * written so that its RESTING state is the still picture: the pearl at its first angle, the mark on
 * its baseline, the ember at opacity 0.
 *
 * The size is the surface's: a second class sets `width` and `height`. */

@property --pearl-ang {
  syntax: "<angle>";
  inherits: false;
  initial-value: 0deg;
}

.pearl {
  position: relative;
  display: inline-block;
  flex: none;
}

.layer {
  position: absolute;
  inset: var(--space-0);
  mask-image: url("/brand/mark.png");
  mask-repeat: no-repeat;
  mask-position: center;
  mask-size: contain;
}

.ghost {
  background: var(--text);
  opacity: 0.1;
}

/* The pearl: the canvas's cone, its start angle turning. */
.ink {
  background-image: conic-gradient(
    from var(--pearl-ang) at 50% 45%,
    var(--buddy-pearl-a),
    var(--buddy-pearl-b),
    var(--buddy-pearl-c),
    var(--buddy-pearl-b),
    var(--buddy-pearl-a)
  );
  animation-name: pearl-turn;
  animation-duration: var(--dur-buddy-rest);
  animation-timing-function: var(--ease-linear);
  animation-iteration-count: infinite;
}

/* The disclosure line's small mark stands still. */
.still .ink {
  animation-name: none;
}

.arrival {
  background-image: conic-gradient(
    from var(--pearl-ang) at 50% 45%,
    var(--buddy-pearl-arrival-a),
    var(--buddy-pearl-arrival-b),
    var(--buddy-pearl-arrival-c),
    var(--buddy-pearl-arrival-d),
    var(--buddy-pearl-arrival-a)
  );
  opacity: 0;
}

.highlight {
  background: var(--buddy-pearl-highlight);
}

/* Buddy works: the pearl turns faster and the whole mark lifts like a repetition. */
.work {
  animation-name: pearl-rep;
  animation-duration: var(--dur-buddy-work);
  animation-timing-function: var(--ease-buddy-rep);
  animation-iteration-count: infinite;
}

.work .ink {
  animation-duration: var(--dur-buddy-work);
}

/* An answer arrived: one ember run, turning at its own pace, then back to the pearl. It plays when
 * the class is applied and never again while it stays, so a re-render does not replay it. */
.arrive .arrival {
  animation:
    pearl-arrival var(--dur-buddy-arrival) var(--ease) both,
    pearl-turn var(--dur-buddy-arrival-spin) var(--ease-linear) infinite;
}

@keyframes pearl-turn {
  to {
    --pearl-ang: 360deg;
  }
}

@keyframes pearl-rep {
  0%,
  100% {
    transform: translateY(0) scale(1);
  }

  45% {
    transform: translateY(-4%) scale(1.03);
  }
}

@keyframes pearl-arrival {
  0%,
  70% {
    opacity: 1;
  }

  100% {
    opacity: 0;
  }
}
